import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import BannerBlock from './BannerBlock/BannerBlock'
import { fetchFacilities } from '../../services/api'
import style from './unitspage.module.scss'
import krasnodarMap from '../../assets/krasnodar.png'
import { useAccessibility } from '../../context/AccessibilityContext/AccessibilityContext'
import AccessibleWrapper from '../../components/AccessibleWrapper/AccessibleWrapper'
import SearchBar from '../../components/SearchBar/SearchBar'

const UnitsPage = () => {
    const [facilities, setFacilities] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedFacility, setSelectedFacility] = useState(null)
    const [showAllCoaches, setShowAllCoaches] = useState(false)
    const [showAllPrograms, setShowAllPrograms] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const navigate = useNavigate()
    const { settings } = useAccessibility()

    useEffect(() => {
        const loadFacilities = async () => {
            try {
                const { data } = await fetchFacilities()
                console.log('Facilities data:', data) // Логируем данные
                setFacilities(data)
                if (data.length > 0) setSelectedFacility(data[0])
            } catch (error) {
                console.error('Error loading facilities:', error)
            } finally {
                setLoading(false)
            }
        }
        
        loadFacilities()
    }, [])

    const handleFacilitySelect = (facility) => {
        console.log('Selected facility:', facility)
        setSelectedFacility(facility)
        setShowAllCoaches(false)
        setShowAllPrograms(false)
    }

    const handleSearch = (query) => {
        setSearchQuery(query)
        if (!query.trim()) {
            setSearchResults([])
            return
        }
        
        const results = facilities.filter(facility => {
            const address = facility.attributes?.Address || ''
            const title = facility.attributes?.Title || ''
            return address.toLowerCase().includes(query.toLowerCase()) ||
                   title.toLowerCase().includes(query.toLowerCase())
        })
        setSearchResults(results)
        
        // Автоматически выбираем первый результат поиска
        if (results.length > 0) {
            setSelectedFacility(results[0])
        }
    }

    // Обработчик выбора из результатов поиска
    const handleSearchSelect = (item) => {
        console.log('Search result selected:', item)
        if (item.type === 'facility') {
            const facility = facilities.find(f => f.id === item.id)
            if (facility) {
                handleFacilitySelect(facility)
            }
        }
    }

    // Безопасное получение данных - проверяем существование полей
    const displayedCoaches = selectedFacility?.attributes?.coaches?.data?.slice(
        0, showAllCoaches ? undefined : 3
    ) || []

    const displayedPrograms = selectedFacility?.attributes?.programs?.data?.slice(
        0, showAllPrograms ? undefined : 3
    ) || []

    const getContrastColor = () => {
        return settings.contrast !== 'default' ? 'var(--text-primary)' : '#1d3e8c'
    }

    const getBackgroundColor = () => {
        return settings.contrast !== 'default' ? 'var(--bg-primary)' : 'rgba(255, 255, 255, 0.85)'
    }

    return (
        <AccessibleWrapper 
            background={getBackgroundColor()}
            color={getContrastColor()}
        >
            <div className={style.unitspage}>
                <BannerBlock />
                
                <div className={style.searchWrapper}>
                    <SearchBar 
                        placeholder="Найти филиал по городу или району"
                        searchType="facilities"
                        onSearch={handleSearch}
                        onSelect={handleSearchSelect} // Добавлен обработчик выбора
                        customResults={searchResults.map(facility => ({
                            id: facility.id,
                            title: facility.attributes?.Title || 'Без названия',
                            text: facility.attributes?.Address || 'Адрес не указан',
                            type: 'facility'
                        }))}
                    />
                </div>

                <div className={style.container}>
                    {/* Список всех филиалов */}
                    <div className={style.facilitiesList}>
                        <h3>Все филиалы ({facilities.length})</h3>
                        <div className={style.facilitiesGrid}>
                            {facilities.map(facility => (
                                <div 
                                    key={facility.id}
                                    className={`${style.facilityCard} ${selectedFacility?.id === facility.id ? style.active : ''}`}
                                    onClick={() => handleFacilitySelect(facility)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleFacilitySelect(facility)}
                                    tabIndex="0"
                                    role="button"
                                    aria-label={`Выбрать филиал ${facility.attributes?.Title || ''}`}
                                >
                                    <h4>{facility.attributes?.Title || 'Без названия'}</h4>
                                    <p className={style.address}>{facility.attributes?.Address || 'Адрес не указан'}</p>
                                    <p className={style.phone}>{facility.attributes?.Phone || 'Телефон не указан'}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={style.mapContainer} aria-label="Карта филиалов">
                        <img 
                            src={krasnodarMap} 
                            alt="Карта Краснодарского края с филиалами" 
                            className={`${style.mapImage} keep-image`}
                            useMap="#facilitiesMap"
                            aria-hidden={!settings.images}
                        />
                        
                        <map name="facilitiesMap">
                            {facilities.map(facility => (
                                <area
                                    key={facility.id}
                                    shape="circle"
                                    coords={`${facility.attributes?.MapX || facility.attributes?.map_x || 50},${facility.attributes?.MapY || facility.attributes?.map_y || 50},20`}
                                    alt={facility.attributes?.Title || 'Филиал'}
                                    title={facility.attributes?.Title || 'Филиал'}
                                    onClick={() => handleFacilitySelect(facility)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleFacilitySelect(facility)}
                                    tabIndex="0"
                                    role="button"
                                />
                            ))}
                        </map>

                        {facilities.map(facility => {
                            const mapX = facility.attributes?.MapX || facility.attributes?.map_x
                            const mapY = facility.attributes?.MapY || facility.attributes?.map_y
                            
                            if (mapX === null || mapY === null || mapX === undefined || mapY === undefined) {
                                return null
                            }
                            
                            return (
                                <button
                                    key={facility.id}
                                    className={`${style.mapMarker} ${selectedFacility?.id === facility.id ? style.active : ''}`}
                                    style={{
                                        left: `${mapX}%`,
                                        top: `${mapY}%`,
                                        color: settings.contrast === 'dark' ? '#fff' : '#e22b36',
                                    }}
                                    onClick={() => handleFacilitySelect(facility)}
                                    aria-label={`Филиал ${facility.attributes?.Title || ''}`}
                                    tabIndex="0"
                                >
                                    ★
                                </button>
                            )
                        })}
                    </div>

                    {/* Информация о выбранном филиале */}
                    <div className={style.infoContainer}>
                        {selectedFacility && (
                            <>
                                <div className={style.facilityInfo}>
                                    <h2>{selectedFacility.attributes?.Title || 'Без названия'}</h2>
                                    <div className={style.infoRow}>
                                        <span>Адрес:</span>
                                        <p>{selectedFacility.attributes?.Address || 'Адрес не указан'}</p>
                                    </div>
                                    <div className={style.infoRow}>
                                        <span>Телефон:</span>
                                        <p>{selectedFacility.attributes?.Phone || 'Телефон не указан'}</p>
                                    </div>
                                    <button 
                                        className={style.detailsButton}
                                        onClick={() => navigate(`/unit/${selectedFacility.id}`)}
                                    >
                                        Подробнее
                                    </button>
                                </div>

                                <div className={style.programsSection}>
                                    <h3>Программы</h3>
                                    <div className={style.programsList}>
                                        {displayedPrograms.length > 0 ? (
                                            displayedPrograms.map(program => (
                                                <div key={program.id} className={style.programCard}>
                                                    <h4>{program.attributes?.Title || 'Без названия'}</h4>
                                                    <p className={style.price}>{program.attributes?.Price || 'Цена не указана'}</p>
                                                    <p className={style.description}>{program.attributes?.Description || 'Описание отсутствует'}</p>
                                                </div>
                                            ))
                                        ) : (
                                            <p className={style.noData}>Нет данных о программах</p>
                                        )}
                                    </div>
                                    {(selectedFacility.attributes?.programs?.data?.length || 0) > 3 && (
                                        <button 
                                            className={style.showMoreButton}
                                            onClick={() => setShowAllPrograms(!showAllPrograms)}
                                        >
                                            {showAllPrograms ? 'Скрыть' : 'Показать все'}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </AccessibleWrapper>
    )
}

export default UnitsPage